import type { FC } from "react"
import React, { memo, useRef } from "react"

import { centerPanelWithinPadding } from "@logic/lib/design"
import type { SetterOrUpdater } from "recoil"
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil"

import { panelBeingDraggedState } from "~/containers/LegacyDesigner/state/elements/panels/dragging"
import { findPanelValidityState } from "~/containers/LegacyDesigner/state/elements/panels/validation"
import { useActivateOnePanel } from "~/containers/LegacyDesigner/state/elements/transactions/activatePanel"
import { useDeletePanels } from "~/containers/LegacyDesigner/state/elements/transactions/deletePanels"
import { phaseState } from "~/containers/LegacyDesigner/state/phase"
import { zoomState } from "~/containers/LegacyDesigner/state/view"

import { HatchPattern, TilePattern } from "./Patterns"
import {
  findProjectedPanelState,
  hoveredPanelState,
  panelFillState,
  panelGridGapState,
  panelSelectionPositionState,
  panelSelectionState,
} from "../../../state/elements/panels"
import { shiftIsDownState } from "../../../state/inputs"
import { toolState } from "../../../state/tool"
import type { MouseControlsPerTool, Tool } from "../../../state/tool"
import { Drag } from "../../Interactions"

export type PanelColorSet = {
  fg: string
  bg: string
}

export type PanelActive = `Active` | `Inactive` | `Normal`

export type PanelValid = `Error` | `Valid` | `Warning`

export const PANEL_COLOR_SETS: Record<
  PanelActive,
  Record<PanelValid, PanelColorSet>
> = {
  Active: {
    Valid: {
      fg: `#0b8`,
      bg: `#ded8`,
    },
    Warning: {
      fg: `#0b8`,
      bg: `orange`,
    },
    Error: {
      fg: `#0b8`,
      bg: `red`,
    },
  },
  Inactive: {
    Valid: {
      fg: `#e40`,
      bg: `#edd8`,
    },
    Warning: {
      fg: `#e40`,
      bg: `orange`,
    },
    Error: {
      fg: `#e40`,
      bg: `red`,
    },
  },
  Normal: {
    Valid: {
      fg: `#0008`,
      bg: `#ddd8`,
    },
    Warning: {
      fg: `#0008`,
      bg: `orange`,
    },
    Error: {
      fg: `#0008`,
      bg: `red`,
    },
  },
}

const Panel_Internal: FC<{
  id: string
  zoom: number
  gridGap: number
  phase: string
  transparency: string
  tool: Tool
  shiftIsDown: boolean
  setPanelSelection: SetterOrUpdater<Set<string>>
  panelSelectionHasId: boolean
}> = ({
  id,
  zoom,
  gridGap,
  phase,
  transparency,
  tool,
  shiftIsDown,
  setPanelSelection,
  panelSelectionHasId,
}) => {
  const panel = useRecoilValue(findProjectedPanelState(id))
  // console.log(`panel rerendering ${id}`)

  const nodeRef = useRef(null)

  const displaceSelection = useSetRecoilState(panelSelectionPositionState)
  const setPanelBeingDragged = useSetRecoilState(panelBeingDraggedState)
  const isSelected = panelSelectionHasId

  const activatePanel = useActivateOnePanel()
  const deletePanels = useDeletePanels()
  const panelValidity = useRecoilValue(findPanelValidityState(id))
  // const refresh = useRecoilRefresher_UNSTABLE(findPanelValidityState(id))
  // useEffect(() => {
  //   refresh()
  // }, [refresh])

  // const [startDragSelect, finishDragSelect] = useDragSelect()
  const setHoveredPanel = useSetRecoilState(hoveredPanelState)

  const mouseDownFor: MouseControlsPerTool = {
    Select: (e) => {
      e.stopPropagation()
      if (shiftIsDown) {
        setPanelSelection((current) => {
          if (current.has(id)) {
            current.delete(id)

            return current
          }

          return current.add(id)
        })
        // setting start point state updates the selected panels immediately
        // startDragSelect()
        // finishDragSelect()
      } else if (!panelSelectionHasId) {
        setPanelSelection(new Set([id]))
      }
    },
    PanelActivator: (e) => (e.stopPropagation(), activatePanel(id)),
    PanelEraser: (e) => (e.stopPropagation(), deletePanels(id)),
  }

  const currentPosition = {
    x: panel.xSnap ?? panel.x,
    y: panel.ySnap ?? panel.y,
  }
  const activeness: PanelActive =
    phase !== `Panel Activation`
      ? `Normal`
      : panel.isActive
        ? `Active`
        : `Inactive`

  const { fg, bg } = PANEL_COLOR_SETS[`Normal`][panelValidity]

  const panelWithoutGap = centerPanelWithinPadding({
    panel,
    gap: gridGap,
  })

  return phase === `Panel Activation` && panelValidity === `Error` ? null : (
    <Drag
      tool="Select"
      position={panel}
      onDrag={(e) => (setPanelBeingDragged(id), displaceSelection(e))}
      onStop={() => setPanelBeingDragged(null)}
      nodeRef={nodeRef}
      onMouseDown={mouseDownFor[tool]}
    >
      <g
        ref={nodeRef}
        onMouseEnter={() => setHoveredPanel(id)}
        onMouseLeave={() => setHoveredPanel(``)}
      >
        <HatchPattern fill={bg} stroke={bg} id={id} />
        <TilePattern
          id={id}
          zoom={zoom}
          fill={
            transparency === `Transparent` || activeness === `Inactive`
              ? `transparent`
              : fg
          }
        />
        <rect
          style={{ transformBox: `fill-box` }}
          transform={`rotate(${panel.r})`}
          x={currentPosition.x}
          y={currentPosition.y}
          width={panel.w}
          height={panel.h}
          r={isSelected ? 7 : 6}
          fill={`transparent`}
          stroke={
            transparency === `Transparent`
              ? activeness === `Active`
                ? `#0b8`
                : fg
              : undefined
          }
          strokeWidth={3}
        />
        <rect
          style={{ transformBox: `fill-box` }}
          transform={`rotate(${panel.r})`}
          x={currentPosition.x}
          y={currentPosition.y}
          width={panel.w}
          height={panel.h}
          r={isSelected ? 7 : 6}
          fill={
            transparency === `Transparent` || activeness === `Inactive`
              ? `transparent`
              : bg
          }
          stroke={
            transparency === `Transparent` || activeness === `Inactive`
              ? bg
              : undefined
          }
          strokeWidth={2}
        />
        <rect
          style={{ transformBox: `fill-box` }}
          transform={`rotate(${panel.r})`}
          x={panelWithoutGap.x}
          y={panelWithoutGap.y}
          width={panelWithoutGap.w}
          height={panelWithoutGap.h}
          r={isSelected ? 7 : 6}
          fill={`url(#pattern-tile-${id})`}
        />
        {isSelected && (
          <rect
            style={{ transformBox: `fill-box` }}
            transform={`rotate(${panel.r})`}
            x={currentPosition.x}
            y={currentPosition.y}
            width={panel.w}
            height={panel.h}
            r={isSelected ? 7 : 6}
            fill={`url(#pattern-hatch-${id})`}
          />
        )}
      </g>
    </Drag>
  )
}
export const Panel = memo(Panel_Internal)

const StudioPanels_Internal: React.FC<{
  ids: string[]
}> = ({ ids }) => {
  const zoom = useRecoilValue(zoomState)
  const gridGap = useRecoilValue(panelGridGapState)
  const shiftIsDown = useRecoilValue(shiftIsDownState)
  const tool = useRecoilValue(toolState)
  const phase = useRecoilValue(phaseState)
  const [panelSelection, setPanelSelection] = useRecoilState(panelSelectionState)
  const transparency = useRecoilValue(panelFillState)

  return (
    <>
      {ids.map((id) => (
        <Panel
          key={id}
          id={id}
          zoom={zoom}
          gridGap={gridGap}
          phase={phase}
          transparency={transparency}
          tool={tool}
          shiftIsDown={shiftIsDown}
          setPanelSelection={setPanelSelection}
          panelSelectionHasId={panelSelection.has(id)}
        />
      ))}
    </>
  )
}

export const StudioPanels = memo(StudioPanels_Internal)
